import { createContext, useContext, useMemo, useState } from 'react';
import { FitViewOptions, ViewportHelperFunctionOptions } from 'reactflow';

import { InteractionDagNode } from '../dag/internalDag';
import { Lens } from '../dag/InternalDagInterfaces';
import { DbtDagNode } from '../dbt-dag';
import { OnNodeClick } from '../hooks/useEventQueue';

export type LensState = 'success' | 'info' | 'error';
export interface LensStateInfo {
  state: LensState;
  description: string;
  descriptionDetails: string;
}

export type LensContext = {
  queryCountMax: number;
  queryCountMin: number;
  costMax: number;
  costMin: number;
  lensState?: Map<Lens | 'default', LensStateInfo>;
};
interface PublicDagProviderProps<TDbtDagNode extends DbtDagNode> {
  disableMagnifiedNode: boolean;
  onNodeClick: OnNodeClick<TDbtDagNode>;
  onNodeDoubleClick: OnNodeClick<TDbtDagNode>;
  onNodeRightClick: OnNodeClick<TDbtDagNode>;
  updateHighlightedNode: (selected: string[] | undefined) => void;
  hideContextMenu: () => void;
  clearSelected: () => void;
  selectedNode: InteractionDagNode<TDbtDagNode> | undefined;
  largeDagWarningShown: boolean | undefined;
  setLargeDagWarningShown: (largeDagWarningShown: boolean) => void;
  setSelected: (node: InteractionDagNode<TDbtDagNode>) => void;
  resetDagLayoutAndZoom: () => void;
  lens: Lens | null;
  setLens: (lens: Lens | null) => void;
  lensContext: LensContext;
  setLensContext: React.Dispatch<React.SetStateAction<LensContext>>;
  zoomIn: (options?: ViewportHelperFunctionOptions) => void;
  zoomOut: (options?: ViewportHelperFunctionOptions) => void;
  fitView: (options?: FitViewOptions) => void;
}

interface PrivateDagProviderProps<TDbtDagNode extends DbtDagNode>
  extends PublicDagProviderProps<TDbtDagNode> {
  children: React.ReactNode;
  /**
   * Only true if trying to access a context from within
   * a context provider.
   */
  isInContext: boolean;
  /**
   * A queue that keeps track of events that need to occur
   * in the DAG. This is primarily used to handle
   * actions external to the DAG that must impact the DAG.
   */
  eventQueue: DagEvent<TDbtDagNode>[];
  /**
   * Call this after we've handled all the events in the queue.
   */
  clearEventQueue: () => void;
}

export const DagContext = createContext<Omit<PrivateDagProviderProps<any>, 'children'>>(
  {
    disableMagnifiedNode: false,
    isInContext: false,
    onNodeClick: () => undefined,
    onNodeDoubleClick: () => undefined,
    onNodeRightClick: () => undefined,
    updateHighlightedNode: () => undefined,
    hideContextMenu: () => undefined,
    selectedNode: undefined,
    largeDagWarningShown: undefined,
    setLargeDagWarningShown: () => {},
    clearSelected: () => {},
    setSelected: () => {},
    resetDagLayoutAndZoom: () => {},
    eventQueue: [],
    clearEventQueue: () => {},
    lens: null,
    setLens: () => {},
    lensContext: {
      queryCountMin: 0,
      queryCountMax: 0,
      costMax: 0,
      costMin: 0,
    },
    setLensContext: () => {},
    zoomIn: () => {},
    zoomOut: () => {},
    fitView: () => {},
  },
);

export const useDagContext = <
  TDbtDagNode extends DbtDagNode,
>(): PublicDagProviderProps<TDbtDagNode> => {
  return useContext(DagContext);
};

type DagEvent<TDbtDagNode extends DbtDagNode> =
  | {
      type: 'resetDagLayoutAndZoom' | 'hideContextMenu';
    }
  | {
      type: 'updateHighlightedNodes';
      nodeIds: string[];
    }
  | {
      type: 'nodeClick' | 'nodeDoubleClick' | 'nodeRightClick';
      event: React.MouseEvent<Element, MouseEvent>;
      node: InteractionDagNode<TDbtDagNode>;
    }
  | {
      type: 'zoomIn' | 'zoomOut';
      options?: ViewportHelperFunctionOptions;
    }
  | {
      type: 'fitView';
      options?: FitViewOptions;
    };

const DagProviderImpl = <TDbtDagNode extends DbtDagNode>({
  options = {},
  children,
}: {
  children: React.ReactNode;
  options?: {
    disableMagnifiedNode?: boolean;
  };
}) => {
  const { disableMagnifiedNode = false } = options;
  const [selectedNode, setSelectedNode] = useState<
    InteractionDagNode<TDbtDagNode> | undefined
  >(undefined);
  const [eventQueue, updateEventQueue] = useState<DagEvent<TDbtDagNode>[]>([]);

  const combinedOnClickFn: OnNodeClick<TDbtDagNode> = (event, node) => {
    setSelectedNode(node);
    updateEventQueue((q) => [...q, { type: 'nodeClick', node, event }]);
  };

  const [lens, setLens] = useState<Lens | null>(null);
  const [lensContext, setLensContext] = useState<LensContext>({
    queryCountMin: 0,
    queryCountMax: 0,
    costMax: 0,
    costMin: 0,
    lensState: new Map<Lens, LensStateInfo>(),
  });
  const [largeDagWarningShown, setLargeDagWarningShown] = useState<boolean | undefined>(
    false,
  );
  /**
   * TODO: refactor how we handle hiding context menus!
   * This is clunky but allows us to avoid checking if a context menu
   * is open every time we do any interaction in the DAG
   */
  const [mightHaveContextMenu, setMightHaveContextMenu] = useState(0);

  return (
    <DagContext.Provider
      value={useMemo(() => {
        const addToEventQueue = (event: DagEvent<TDbtDagNode>) => {
          updateEventQueue((q) => [...q, event]);
        };

        return {
          disableMagnifiedNode,
          isInContext: true,

          onNodeClick: combinedOnClickFn,
          onNodeDoubleClick: (event, node) => {
            addToEventQueue({ type: 'nodeDoubleClick', node, event });
          },
          onNodeRightClick: (event, node) => {
            setMightHaveContextMenu(2);
            addToEventQueue({ type: 'nodeRightClick', node, event });
          },
          updateHighlightedNode: (nodeIds: string[] | undefined = []) => {
            addToEventQueue({ type: 'updateHighlightedNodes', nodeIds });
          },
          hideContextMenu: () => {
            if (!mightHaveContextMenu) return;
            addToEventQueue({ type: 'hideContextMenu' });
            setMightHaveContextMenu((ct) => ct - 1);
          },
          selectedNode,
          largeDagWarningShown,
          setLargeDagWarningShown,
          clearSelected: () => {
            setSelectedNode(undefined);
          },
          setSelected: (node: InteractionDagNode<TDbtDagNode>) => {
            setSelectedNode(node);
          },
          resetDagLayoutAndZoom: () => {
            updateEventQueue((q) => [...q, { type: 'resetDagLayoutAndZoom' }]);
          },
          clearEventQueue: () => {
            updateEventQueue([]);
          },
          eventQueue,
          lens,
          setLens,
          lensContext,
          setLensContext,
          zoomIn: (options?: ViewportHelperFunctionOptions) => {
            updateEventQueue((q) => [...q, { type: 'zoomIn', options: options }]);
          },
          zoomOut: (options?: ViewportHelperFunctionOptions) => {
            updateEventQueue((q) => [...q, { type: 'zoomOut', options: options }]);
          },
          fitView: (options?: FitViewOptions) => {
            updateEventQueue((q) => [...q, { type: 'fitView', options: options }]);
          },
        };
      }, [
        disableMagnifiedNode,
        selectedNode,
        largeDagWarningShown,
        eventQueue,
        lens,
        lensContext,
        mightHaveContextMenu,
      ])}
    >
      {children}
    </DagContext.Provider>
  );
};

export const DagProvider = ({
  /**
   * If set to false, this DagProvider will not overwrite the context if it is already set.
   */
  overrideContext = true,
  ...props
}: {
  children: React.ReactNode;
  overrideContext?: boolean;
  options?: {
    disableMagnifiedNode?: boolean;
  };
}) => {
  const dagContext = useContext(DagContext);
  if (!overrideContext && dagContext.isInContext) {
    return <>{props.children}</>;
  }
  return <DagProviderImpl {...props} />;
};
